/** Pure pairing / rollup / pick logic for the comparison screen.
 *  Kept free of React so it can be unit-tested directly. */
import type { ComparisonField, ComparisonRun, EvalMultiModelTestDetail } from "@api/rest/types-and-hooks";
import { canonicalizeLlmOutput } from "@components/LlmOutput/outputShapes";
import {
  ArmState,
  type ArmStateValue,
  OutputMatch,
  type OutputMatchValue,
  PairwiseStatus,
  RunStatus,
  Verdict,
  type WinnerValue,
} from "@components/ModelTesting/constants";

export const armState = (run: ComparisonRun | null): ArmStateValue => {
  if (run === null) return ArmState.Pending;
  if (run.status === RunStatus.Complete) return ArmState.Complete;
  if (run.status === RunStatus.Failed) return ArmState.Failed;
  return ArmState.Running;
};

export interface ComparisonProgress {
  /** Every (field, arm) slot — the denominator of the progress line. */
  total: number;
  complete: number;
  running: number;
  failed: number;
  pending: number;
}

export const comparisonProgress = (fields: ComparisonField[]): ComparisonProgress => {
  const progress: ComparisonProgress = { total: fields.length * 2, complete: 0, running: 0, failed: 0, pending: 0 };
  for (const field of fields) {
    for (const run of [field.baseline, field.candidate]) {
      progress[armState(run)] += 1;
    }
  }
  return progress;
};

/** Both arms complete, outputs different, and the judge in flight or still
 *  owed a start — the server's needs_judge, seen from the client. Identical
 *  outputs never get a judge call (older ones keep a null status). */
export const awaitingJudge = (f: ComparisonField): boolean =>
  armState(f.baseline) === ArmState.Complete &&
  armState(f.candidate) === ArmState.Complete &&
  outputsCompared(f) !== OutputMatch.Identical &&
  (f.pairwise_status === null ||
    f.pairwise_status === PairwiseStatus.Pending ||
    f.pairwise_status === PairwiseStatus.Running);

/** True while any slot could still change on its own — the polling signal.
 *  Pending slots don't count: nothing comes until someone starts the run. */
// Judges advance only on a poll, so an awaited judge keeps the poll alive.
export const anyRunning = (fields: ComparisonField[]): boolean =>
  fields.some(
    (f) => armState(f.baseline) === ArmState.Running || armState(f.candidate) === ArmState.Running || awaitingJudge(f),
  );

/** Identical/different once both arms are complete, null before that.
 *  Compared on the canonical form, not raw bytes. */
export const outputsCompared = (field: ComparisonField): OutputMatchValue | null => {
  if (armState(field.baseline) !== ArmState.Complete || armState(field.candidate) !== ArmState.Complete) return null;
  return canonicalizeLlmOutput(field.baseline!.result) === canonicalizeLlmOutput(field.candidate!.result)
    ? OutputMatch.Identical
    : OutputMatch.Different;
};

export interface DraftPick {
  winner: WinnerValue;
  note: string;
}

export type DraftPicks = Record<string, DraftPick>;

/** Set a field's pick; clicking the selected winner clears it. Notes survive
 *  a winner change but leave with the pick. */
export const togglePick = (picks: DraftPicks, promptKey: string, winner: WinnerValue): DraftPicks => {
  const next = { ...picks };
  if (next[promptKey]?.winner === winner) {
    delete next[promptKey];
  } else {
    next[promptKey] = { winner, note: next[promptKey]?.note ?? "" };
  }
  return next;
};

export const setPickNote = (picks: DraftPicks, promptKey: string, note: string): DraftPicks => {
  const existing = picks[promptKey];
  if (!existing) return picks; // a note without a pick has nothing to explain
  return { ...picks, [promptKey]: { ...existing, note } };
};

/** The PATCH payload: every drafted pick, plus an explicit clear (winner: "")
 *  for saved picks the drafts dropped — the server upserts per key. */
/** ``saved`` is what the server holds (the baseline), not the last fetch —
 *  a refetch may not have landed since the previous save. */
export const picksForPatch = (
  picks: DraftPicks,
  saved: DraftPicks,
): { prompt_key: string; winner: string; note: string }[] => {
  const cleared = Object.keys(saved)
    .filter((key) => !picks[key])
    .map((prompt_key) => ({ prompt_key, winner: "", note: "" }));
  return [
    ...Object.entries(picks).map(([prompt_key, pick]) => ({ prompt_key, winner: pick.winner, note: pick.note })),
    ...cleared,
  ];
};

/** The saved state the drafts are compared against. Held client-side and
 *  updated the moment a save succeeds — deriving it from the query cache
 *  left a dirty window (and dialog nags) until the refetch round-tripped. */
export interface FeedbackBaseline {
  picks: DraftPicks;
  notes: string;
  verdict: string;
}

export const baselineFromComparison = (comparison: {
  notes: string;
  verdict: string;
  fields: ComparisonField[];
}): FeedbackBaseline => ({
  picks: draftPicksFromFields(comparison.fields),
  notes: comparison.notes,
  verdict: comparison.verdict || Verdict.Undecided,
});

/** True when the drafts differ from the baseline — the signal for the
 *  footer's "Unsaved changes" and the leave guards. */
export const hasUnsavedFeedback = (
  baseline: FeedbackBaseline,
  picks: DraftPicks,
  notes: string,
  verdict: string,
): boolean => {
  if (notes !== baseline.notes) return true;
  if (verdict !== baseline.verdict) return true;
  const keys = new Set([...Object.keys(baseline.picks), ...Object.keys(picks)]);
  for (const key of keys) {
    const before = baseline.picks[key];
    const after = picks[key];
    if (!before !== !after) return true;
    if (before && after && (before.winner !== after.winner || before.note !== after.note)) return true;
  }
  return false;
};

/** Seed the local pick state from what the server already recorded. */
export const draftPicksFromFields = (fields: ComparisonField[]): DraftPicks => {
  const picks: DraftPicks = {};
  for (const field of fields) {
    if (field.pick) {
      picks[field.prompt_key] = { winner: field.pick.winner as WinnerValue, note: field.pick.note };
    }
  }
  return picks;
};

/** Flagged items still without a saved pick, in queue order. */
export const unreviewedQueueItems = <T extends { winner: string | null }>(queue: T[]): T[] =>
  queue.filter((item) => !item.winner);

/** The multi-model detail with this comparison's saved picks applied to its
 *  review items, so the queue reads reviewed before the slow refetch lands. */
export const withSavedPicks = (
  detail: EvalMultiModelTestDetail,
  comparisonId: number,
  picks: DraftPicks,
): EvalMultiModelTestDetail => ({
  ...detail,
  models: detail.models.map((m) => ({
    ...m,
    review: m.review.map((item) =>
      item.comparison_id === comparisonId ? { ...item, winner: picks[item.prompt_key]?.winner ?? null } : item,
    ),
  })),
});
